import { useEffect, useRef, useState } from 'react';

import { useRouter } from 'next/router';
import {
  applyPromoCodeNew,
  getSubscriptionPlansSelectionDataV2
} from 'src/services/apiRequests/SubscriptionService';
import { PromoResponseItemNew } from 'src/services/responseTypes/applyPromoResponse';
import { Plan } from 'src/services/responseTypes/displayPlansResponse';
import { SubscriptionApiResponse } from 'src/services/responseTypes/subscriptionApiResponse';
import { CoverSection, SubscriptionResponseV2 } from 'src/types/subscription';
import { putFacebookEvent } from 'src/utils/analytics/fbpixel/fbCapiHelper';
import { gtm } from 'src/utils/analytics/gtm';
import { mixpanelObject } from 'src/utils/analytics/mixpanel';
import {
  MIXPANEL_BUTTON_TYPE,
  MIXPANEL_CTA,
  MIXPANEL_ELEMENT,
  MIXPANEL_FAILURE_REASON,
  MIXPANEL_PACK_SELECT_CTA,
  MIXPANEL_POP_UP_NAME,
  MIXPANEL_PROMO_CODE,
  MIXPANEL_PROMO_CODE_TYPE,
  MIXPANEL_SUCCESS,
  MIXPANEL_UNIQUE_ID
} from 'src/utils/analytics/mixpanel/mixpanelConstants';
import {
  ctaEvent,
  popupCTAEvent,
  promoCodeResultEvent,
  subscriptionSelectedEvent
} from 'src/utils/analytics/mixpanel/mixpanelEventsHelper';
import {
  deleteLocalStorage,
  getLocalStorage,
  setLocalStorage
} from 'src/utils/browserStorageHandler';
import { globalUrlHelper } from 'src/utils/common';
import { ActivePromotions, DynamicObject } from 'src/utils/commonTypes';
import {
  GUEST_TOKEN,
  REFERRAL_CODE,
  REFERRAL_ID,
  REFERRAL_NAME,
  REFERRAL_USERID,
  RSVOD_PACK,
  STUDENT_VERIFICATION_ID,
  USER_TOKEN
} from 'src/utils/constants/localStorageConstants';
import { META_EVENTS } from 'src/utils/constants/metaPixelConstants';
import { FeatureFlagConstants } from 'src/utils/constants/utilConstants';
import { isFeatureEnabled } from 'src/utils/RemoteConfig';
import { fbqTrack } from 'src/utils/scripts/thirdPartyScripts';
import {
  calculateSellPriceNew,
  getCurrencySymbol,
  getCurrentPlanV2,
  getDefaultPlanV2,
  getPlanByIdV2,
  getPromoDiscountNew,
  getSpecialPlanV2,
  goToHome,
  isCohort,
  resetConsumptionPaymentJourneyFlags,
  transformV2PlanCategories,
  transformV2PlanToOldPlan,
  updatePlansDataV2
} from 'src/utils/SubscriptionHelper/SubscriptionHelper';
import { generateUUID } from 'src/utils/UtilityMethods';

import ContextualBanner from './ContextBanner';
import NewuiPlans from './NewuiPlans';
import BadgeTags from './NewuiPlans/BadgeTags/badgetags';
import { PlansWrapper } from './NewuiPlans/styles';
import {
  SUBSCRIPTION_ACTIONS,
  useSubscriptionDispatchV2,
  useSubscriptionStateV2
} from './SubscriptionContext';
import SubscriptionNudge from './subscriptionNudge/SubscriptionNudge';
import {
  ApplyCodeContainer,
  ButtonContainer,
  LoggedInContainerNew,
  PromoIcon,
  RemovePromoCode,
  SubscriptionBody,
  SubscriptionContainerNew,
  SubsDetailContainer,
  TextAndIconContainer,
  TOCLink,
  PlanTitleText,
  ApplyCodeContainerNew,
  DiscountWrapper,
  TOCStyleNew,
  ApplyCodeWrapper,
  LoggedInAndTocWrapper
} from './SubscriptionStylesNew';
import { useAppStore } from '../../hooks/useAppStore';
import ApplyCodePopup from '../applyCodePopup/ApplyCodePopup';
import Button from '../buttons/Button';
import {
  Button as buttonCss,
  rightArrowIcon
} from '../subscriptions/SubscriptionStyle';
import { userOnboarPopupLaunchEvent } from '../userOnboard/MixpanelHelper';

const PROMO_ICON_URL = '/webapp-assets/images/promo_icon.svg';

const TOC = () => {
  const tocText = 'See our {{TOC}} for more details.';
  const tocLinkText = 'Terms of use';
  const splitText = tocText.split('{{TOC}}');
  return (
    <TOCStyleNew data-test-id="tnc-container">
      {splitText[0]}
      <TOCLink data-test-id="tnc-link" href={globalUrlHelper('/termsofuse')}>
        {tocLinkText}
      </TOCLink>
      {splitText?.[1] || ''}
    </TOCStyleNew>
  );
};

const LogggedIn = () => {
  const { translations, usersData } = useAppStore();
  const newSubscriptionStrings = translations.subscription;
  const loginText =
    newSubscriptionStrings.Subscription_Bottomsheet_Already_Loggedin_Text ?? '';
  if (!usersData?.mobile && !usersData?.email) {
    return null;
  }
  const userInfo = usersData?.mobile || usersData?.email;

  return (
    <LoggedInContainerNew data-test-id="subs-login-info">{`${loginText} ${userInfo}`}</LoggedInContainerNew>
  );
};

const ApplyCouponNew = ({
  onClick,
  activePromos,
  promoCode,
  removePromo
}: {
  onClick: () => void;
  activePromos: DynamicObject;
  promoCode: string;
  removePromo: () => void;
}) => {
  const { translations, subscriptionData } = useAppStore();
  const newSubscriptionStrings = translations.subscription;
  const plansData = useSubscriptionStateV2();
  const selectedPlan = getCurrentPlanV2(plansData ?? []);
  const isUpgrade = !!subscriptionData?.maxPricePack?.id;

  if (isUpgrade) return null;
  if (
    !Object.keys(activePromos).length &&
    !isCohort(selectedPlan as SubscriptionResponseV2.Plan)
  ) {
    return (
      <ApplyCodeContainerNew data-test-id="apply-code" onClick={onClick}>
        <TextAndIconContainer>
          {newSubscriptionStrings.CMS_BOTTOMSHEET_APPLYCODE_TEXT}
        </TextAndIconContainer>
        <span css={rightArrowIcon}></span>
      </ApplyCodeContainerNew>
    );
  }

  //cohort case
  if (isCohort(selectedPlan as SubscriptionResponseV2.Plan)) {
    return (
      <ApplyCodeContainer data-test-id="apply-code">
        <TextAndIconContainer>
          <PromoIcon src={PROMO_ICON_URL} />
          {newSubscriptionStrings.COHORT_CODEAPPLIED_TEXT.replace(
            '{{promo_code}}',
            selectedPlan?.price?.code
          )}
        </TextAndIconContainer>
      </ApplyCodeContainer>
    );
  }

  //promo code is valid
  if (activePromos[selectedPlan?.id ?? '']) {
    return (
      <ApplyCodeContainer data-test-id="apply-code">
        <TextAndIconContainer>
          <PromoIcon src={PROMO_ICON_URL} />
          {newSubscriptionStrings.CMS_CODEAPPLIED_SUBTEXT.replace(
            '{{coupon_code_name}}',
            promoCode
          ).replace(
            '{{discounted_percentage}}',
            getPromoDiscountNew(activePromos[selectedPlan?.id ?? ''])
          )}
        </TextAndIconContainer>
        <RemovePromoCode data-test-id="remove-code" onClick={removePromo}>
          {newSubscriptionStrings.CMS_CODEREMOVE_TEXT}
        </RemovePromoCode>
      </ApplyCodeContainer>
    );
  }

  //promoCode is invalid
  return (
    <ApplyCodeContainer data-test-id="apply-code">
      <TextAndIconContainer>
        <PromoIcon src={PROMO_ICON_URL} />
        {promoCode + ' ' + newSubscriptionStrings.INVALID_CODE_TEXT}
      </TextAndIconContainer>
      <RemovePromoCode data-test-id="remove-code" onClick={removePromo}>
        {newSubscriptionStrings.CMS_CODEREMOVE_TEXT}
      </RemovePromoCode>
    </ApplyCodeContainer>
  );
};

const BuyPremium = ({
  onClick,
  label,
  activeOption,
  isAtBestPlan
}: {
  onClick: () => void;
  label?: string;
  deeplink?: string | null;
  activeOption?: SubscriptionResponseV2.Plan | null;
  isAtBestPlan: boolean;
}) => {
  const { translations } = useAppStore();
  const subscriptionStrings = translations.subscription;
  const text = label ? subscriptionStrings[label] || label : '';
  const isExplore = label === 'CMS_PLAN_EXPLORE_PREMIUM_CTA_TEXT';
  const callback = isExplore ? goToHome : onClick;

  return (
    <ButtonContainer data-test-id="payment-box">
      {activeOption?.categoryId !== 'Premium4K' && (
        <DiscountWrapper>
          <BadgeTags
            height="24px"
            text={subscriptionStrings?.CMS_BOTTOM_BEST_VALUE_TEXT}
          />
        </DiscountWrapper>
      )}
      {!isAtBestPlan && (
        <PlanTitleText>
          <img src="/webapp-assets/images/promo_icon.svg" alt="discount" />
          {subscriptionStrings?.CMS_PLAN_BIN_DISCOUNT_TEXT}
        </PlanTitleText>
      )}
      <Button
        data-test-id={
          isExplore ? 'explore-all-premium-button' : 'premium-button'
        }
        callback={callback}
        customStyles={buttonCss}>
        {text}
      </Button>
    </ButtonContainer>
  );
};

interface SubscriptionProps {
  premiumClickHandler: (couponCode?: string) => void;
  plansData: SubscriptionResponseV2.Data | null;
  currentSubscription: SubscriptionApiResponse | null;
  triggerApplyCouponCodePopup: boolean;
  buyPlan: (
    plansData: SubscriptionResponseV2.DurationState[] | null,
    currentPlan: Plan | null,
    selectedPlanId: string,
    activePromos: ActivePromotions,
    promoCode: string,
    countryCode: string
  ) => void;
}

const SubscriptionV4 = (props: SubscriptionProps) => {
  const {
    premiumClickHandler,
    plansData: planDataResponse,
    buyPlan,
    currentSubscription,
    triggerApplyCouponCodePopup
  } = props;
  const { coverSection } = planDataResponse ?? {};

  const router = useRouter();

  const plansDataState = useSubscriptionStateV2();
  const {
    usersLocationData,
    initialLaunchData,
    translations,
    remoteConfig,
    usersData,
    isUserLoggedIn
  } = useAppStore();

  const dispatch = useSubscriptionDispatchV2();

  const countryCode = usersLocationData?.country_code || '';
  const stateCode = usersLocationData?.state_code || '';
  const [applyCodeFlag, setApplyCodeFlag] = useState(false);
  const [activePromos, setActivePromos] = useState<ActivePromotions>({});
  const [prepaidCoupon, setPrepaidCoupon] = useState('');
  const [promoCode, setPromoCode] = useState<string>('');
  const [selectedPlan, setSelectedPlan] = useState<any>();
  const [popupNudge, setPopupNudge] = useState(false);
  const isPromoCodeCheckDone = useRef(false);

  const NEW_SUBSCRIPTION_STRINGS = translations.subscription;
  const premiumButtonText = currentSubscription
    ? NEW_SUBSCRIPTION_STRINGS.CMS_UPGRADE_CTA_TEXT
    : NEW_SUBSCRIPTION_STRINGS.BUY_PREMIUM_CTA;

  const isContextualNudgeEnbled =
    isFeatureEnabled(
      remoteConfig,
      FeatureFlagConstants.FEATURE_SUBSCRIPTION_NUDGE_ENABLED
    ) || false;

  const popupNudgeToggler = () => {
    setPopupNudge((ps) => !ps);
  };

  const trackBuyPremiumMixpanelEvents = () => {
    mixpanelObject.setProperty(MIXPANEL_ELEMENT, premiumButtonText);
    mixpanelObject.setProperty(MIXPANEL_BUTTON_TYPE, MIXPANEL_CTA);
    ctaEvent(false, false, MIXPANEL_PACK_SELECT_CTA);
    mixpanelObject.setProperty(
      MIXPANEL_UNIQUE_ID,
      isUserLoggedIn ? usersData?.id : getLocalStorage(GUEST_TOKEN)
    );
    subscriptionSelectedEvent();
  };

  const buyPremiumOrUpgradeClickHandler = () => {
    trackBuyPremiumMixpanelEvents();
    if (!plansDataState) return null;
    const currentPlan = getCurrentPlanV2(plansDataState);
    const specialPlan = getSpecialPlanV2(plansDataState);
    const specialPlanPrice = specialPlan?.price?.actual ?? 0;
    const isNonSpecialPlanSelected =
      (currentPlan?.price?.actual ?? 0) < specialPlanPrice;
    const referralCode = getLocalStorage(REFERRAL_CODE);
    //check the referral code
    if (
      referralCode &&
      !(currentPlan?.price?.code?.toUpperCase() == referralCode.toUpperCase())
    ) {
      deleteLocalStorage(REFERRAL_ID);
      deleteLocalStorage(REFERRAL_NAME);
      deleteLocalStorage(REFERRAL_CODE);
      deleteLocalStorage(REFERRAL_USERID);
    }
    if (isNonSpecialPlanSelected && isContextualNudgeEnbled) {
      popupNudgeToggler();
      return;
    }
    handleBuyPlanFromNudge();
  };

  const handleBuyPlanFromNudge = (selectedID?: string) => {
    if (!plansDataState) return null;
    // if (
    //   isFeatureEnabled(
    //     remoteConfig,
    //     FeatureFlagConstants.FEATURE_META_PIXEL_INTEGRATION
    //   )
    // ) {
    //   if (countryCode !== CountryCode.IN) {
    //     fbqTrack(META_EVENTS.BuyPremium);
    //     putFacebookEvent(
    //       META_EVENTS.BuyPremium,
    //       new Date().getTime(),
    //       window.location.href
    //     );
    //   }
    // }
    const selectedPlan = selectedID
      ? getPlanByIdV2(selectedID, plansDataState)
      : getCurrentPlanV2(plansDataState);
    const { id: selectedPlanId = '' } = selectedPlan || {};

    buyPlan(
      plansDataState,
      null,
      selectedPlanId,
      activePromos,
      promoCode,
      countryCode
    );

    if (!getLocalStorage(USER_TOKEN)) {
      setPopupNudge(false);
      premiumClickHandler();
    } else {
      if (currentSubscription) {
        setLocalStorage(RSVOD_PACK, currentSubscription);
      }
      window.location.href = 'payment';
    }
  };

  const applyCodeHandler = () => {
    setApplyCodeFlag(!applyCodeFlag);
    if (applyCodeFlag === false) {
      mixpanelObject.setProperty(MIXPANEL_POP_UP_NAME, 'Apply Code Pop Up');
      userOnboarPopupLaunchEvent();
    }
    ctaEvent();
  };
  // set the coupon code
  useEffect(() => {
    if (triggerApplyCouponCodePopup) {
      setApplyCodeFlag(true);
      userOnboarPopupLaunchEvent();
    }
  }, [triggerApplyCouponCodePopup]);

  const checkLoggedIn = (couponVal: string) => {
    premiumClickHandler(couponVal);
    setPrepaidCoupon(couponVal);
  };

  const updateDiscountNew = (
    promoResponse: PromoResponseItemNew,
    promoCodeValue: string
  ) => {
    const activePromotions: ActivePromotions = {};
    promoResponse?.promotionDetails?.productDetails.forEach(async (result) => {
      activePromotions[result.productId] = result;
      const promoSelectedPlan = plansDataState
        ?.flatMap((plan) => plan.options)
        .find((plan) => plan.id === result.productId);

      promoSelectedPlan && setSelectedPlan(promoSelectedPlan);
      if (promoSelectedPlan?.id) {
        dispatch({
          type: SUBSCRIPTION_ACTIONS.UPDATE_PROMO_PLAN,
          payload: { id: promoSelectedPlan?.id }
        });
      } else {
        const singlePlan = await getSinglePlan(result.productId);
        //TODO: fix the type issue
        if (singlePlan && singlePlan.length && plansDataState?.length) {
          dispatch({
            type: SUBSCRIPTION_ACTIONS.UPDATE_SUBSCTIPTION,
            payload: {
              data: updatePlansDataV2(
                plansDataState as any,
                singlePlan as any
              ) as any
            }
          });
          dispatch({
            type: SUBSCRIPTION_ACTIONS.UPDATE_PROMO_PLAN,
            payload: { id: result.productId }
          });
        }
      }
      // fbqTrack(META_EVENTS.Addtocart);
      gtm.packSelectedEventGTM(promoSelectedPlan);
    });
    setActivePromos(activePromotions);
    setPromoCode(promoCodeValue);
  };

  const getSinglePlan = async (planId: string) => {
    if (planId) {
      try {
        const singlePlan = await getSubscriptionPlansSelectionDataV2({
          planId
        });
        if (singlePlan?.data?.duration?.length > 0) {
          return singlePlan.data.duration;
        }
      } catch (error) {
        console.error('Failed to fetch single plan:', error);
      }
    }
    return null;
  };

  const removePromo = () => {
    setActivePromos({});
    setPrepaidCoupon('');
  };
  const getCouponAndIdFromURL = async () => {
    const coupon = router.query?.coupon as string;
    const id = router.query?.id;
    if (coupon && id) {
      const userToken = getLocalStorage(USER_TOKEN);
      isPromoCodeCheckDone.current = true;
      if (!userToken) {
        checkLoggedIn(coupon);
        return;
      }
      try {
        const res = await applyPromoCodeNew(coupon);
        if (res) {
          setLocalStorage(STUDENT_VERIFICATION_ID, id);
          const { code = '', message = '' } = res;
          if (code === 200) {
            {
              updateDiscountNew &&
                updateDiscountNew(res as PromoResponseItemNew, coupon);
              mixpanelObject.setProperty(
                MIXPANEL_PROMO_CODE_TYPE,
                MIXPANEL_PROMO_CODE
              );
              mixpanelObject.setProperty(MIXPANEL_SUCCESS, true);
              mixpanelObject.setProperty(MIXPANEL_FAILURE_REASON, 'N/A');
              mixpanelObject.setProperty(MIXPANEL_PROMO_CODE, coupon);
              promoCodeResultEvent();
            }
          } else {
            mixpanelObject.setProperty(
              MIXPANEL_PROMO_CODE_TYPE,
              MIXPANEL_PROMO_CODE
            );
            mixpanelObject.setProperty(MIXPANEL_SUCCESS, false);
            mixpanelObject.setProperty(
              MIXPANEL_FAILURE_REASON,
              message || "Plan couldn't be found"
            );
            mixpanelObject.setProperty(MIXPANEL_PROMO_CODE, coupon);
            promoCodeResultEvent();
          }
        }
      } catch (error) {
        console.error('plans err =>', error);
        mixpanelObject.setProperty(
          MIXPANEL_PROMO_CODE_TYPE,
          MIXPANEL_PROMO_CODE
        );
        mixpanelObject.setProperty(MIXPANEL_SUCCESS, false);
        mixpanelObject.setProperty(MIXPANEL_FAILURE_REASON, 'Invalid Code');
        mixpanelObject.setProperty(MIXPANEL_PROMO_CODE, coupon);
        promoCodeResultEvent();
      }
      popupCTAEvent();
    }
  };

  useEffect(() => {
    // if plansData changes select default plan to be active
    if (planDataResponse) {
      dispatch({
        type: SUBSCRIPTION_ACTIONS.UPDATE_SUBSCTIPTION,
        payload: {
          data: transformV2PlanCategories(planDataResponse?.duration || [])
        }
      });
    }

    const defaultPlan = getDefaultPlanV2(
      transformV2PlanCategories(planDataResponse?.duration ?? [])
    );

    if (defaultPlan && selectedPlan?.id !== defaultPlan?.id) {
      setSelectedPlan(defaultPlan);
      if (
        isFeatureEnabled(
          remoteConfig,
          FeatureFlagConstants.FEATURE_META_PIXEL_INTEGRATION
        )
      ) {
        const eventId = generateUUID();
        fbqTrack(META_EVENTS.Addtocart, eventId);
        putFacebookEvent(
          META_EVENTS.Addtocart,
          new Date().getTime(),
          window.location.href,
          eventId
        );
      }
      gtm.packSelectedEventGTM(defaultPlan);
    }
  }, [JSON.stringify(planDataResponse)]);

  useEffect(() => {
    resetConsumptionPaymentJourneyFlags();

    if (!isPromoCodeCheckDone.current) {
      getCouponAndIdFromURL();
    }
  }, []);

  //TODO: Check if this is required
  // useEffect(() => {
  //     appConfig.pageType = 'subscriptionPage';
  // }, []);
  let isAtBestPlan = false;
  let buyPremiumLabel = '';
  const activeOption = getCurrentPlanV2(plansDataState) as
    | SubscriptionResponseV2.Plan
    | null
    | undefined;
  const { price } = activeOption ?? {};
  const { currency = 'INR' } = price ?? {};
  const currencySymbol = getCurrencySymbol(currency);
  const amount = `${currencySymbol} ${
    activeOption ? calculateSellPriceNew(activeOption, activePromos) : null
  }`;
  //to-do
  const hasSubscription = !!currentSubscription?.id;
  const hasPlans = (plansDataState?.length || 0) > 0;

  if (hasSubscription && !hasPlans) {
    isAtBestPlan = true;
    buyPremiumLabel = 'CMS_PLAN_EXPLORE_PREMIUM_CTA_TEXT';
  } else {
    buyPremiumLabel =
      NEW_SUBSCRIPTION_STRINGS?.CMS_PAY_CTA_TEXT?.replace(
        '{{plan_price}}',
        amount
      ) || '';
  }
  if (!plansDataState?.length && !hasSubscription) return null;
  return (
    <>
      <SubscriptionContainerNew>
        <ContextualBanner data={coverSection as unknown as CoverSection} />
        <SubscriptionBody>
          <SubsDetailContainer data-test-id="subs-details-container">
            <PlansWrapper>
              <NewuiPlans
                activePromotions={activePromos}
                currentPlan={{
                  ...planDataResponse?.currentPlan,
                  ...currentSubscription
                }}
                isAtBestPlan={isAtBestPlan}
              />
              {!isAtBestPlan && (
                <ApplyCodeWrapper>
                  <ApplyCouponNew
                    promoCode={promoCode}
                    activePromos={activePromos}
                    onClick={applyCodeHandler}
                    removePromo={removePromo}
                  />
                </ApplyCodeWrapper>
              )}
              <LoggedInAndTocWrapper>
                <LogggedIn />
                <TOC />
              </LoggedInAndTocWrapper>
            </PlansWrapper>
            <BuyPremium
              label={buyPremiumLabel}
              deeplink=""
              onClick={buyPremiumOrUpgradeClickHandler}
              activeOption={activeOption}
              isAtBestPlan={isAtBestPlan}
            />
          </SubsDetailContainer>
        </SubscriptionBody>
      </SubscriptionContainerNew>

      {applyCodeFlag && (
        <ApplyCodePopup
          xAccessToken={initialLaunchData?.platform_token?.token || ''}
          show={applyCodeFlag}
          onClose={applyCodeHandler}
          updateDiscountNew={updateDiscountNew}
          newSubscriptionStrings={NEW_SUBSCRIPTION_STRINGS}
          countryCode={countryCode}
          stateCode={stateCode}
          checkLoggedIn={checkLoggedIn}
          prepaidCoupon={prepaidCoupon}
          currentSubscription={currentSubscription}
          clearPrepaid={removePromo}
        />
      )}
      {popupNudge &&
        planDataResponse &&
        plansDataState &&
        isContextualNudgeEnbled && (
          <SubscriptionNudge
            show={popupNudge}
            onClose={popupNudgeToggler}
            currentPlan={transformV2PlanToOldPlan(
              getCurrentPlanV2(plansDataState) as any
            )}
            defaultPlan={transformV2PlanToOldPlan(
              getSpecialPlanV2(plansDataState) as any
            )}
            defaultPlanId={getSpecialPlanV2(plansDataState)?.id ?? ''}
            currentPlanId={selectedPlan?.id}
            buyPlanFromNudge={handleBuyPlanFromNudge}
            activePromos={activePromos}
            newSubscriptionStrings={NEW_SUBSCRIPTION_STRINGS}
          />
        )}
    </>
  );
};

export default SubscriptionV4;
